import React, { useState } from "react";
import {
  ClipboardList,
  Gauge,
  Hash,
  MessageCircleReply,
  MessagesSquare,
  Smile,
  Star,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { Form, ReviewOverviewSnapshot, ReviewReplyStatusCounts, UpsertFormRequest } from "@/services/bedrock";
import { ReviewSourceIcon } from "../components/ReviewSourceIcon";
import { FormCreateAIDialog } from "../Forms/components/FormCreateAIDialog";
import { FormDialog } from "../Forms/components/FormDialog";
import { FormRequestDialog } from "../Forms/components/FormRequestDialog";
import {
  HeroNpsGauge,
  HeroReplyGraphic,
  HeroSentimentChart,
  HeroStarGraphic,
  HeroTenPointScale,
  HeroVolumeGraphic,
  InfoTip,
  MetricHero,
  RatioBar,
} from "./overviewUi";
import { OverviewSnapshotSync } from "./OverviewSnapshotSync";
import { PeriodHealthExportButton } from "./PeriodHealthExportButton";
import { PeriodInsights } from "./PeriodInsights";
import { buildPeriodHealthExportSections, type ExportCountRow } from "./periodHealthExportBuilders";
import { positiveSentimentPercent, type VolumeSeries } from "./snapshotHelpers";
import { cn } from "@/lib/utils";
import { kFormatter } from "@/utils";
import type { XlsxTableSection } from "@/utils/xlsxExportHelpers";

type CountLike = number | null | undefined;

function num(value: CountLike): number {
  return value ?? 0;
}

function delta(current: number, previous: number | null | undefined, hasPrevious: boolean): number | null {
  if (!hasPrevious || previous == null) return null;
  return current - previous;
}

function formatInt(value: number): string {
  if (value === 0) return "0";
  if (value < 0) return `-${kFormatter(Math.abs(value))}`;
  return kFormatter(value);
}

function formatDecimal(value: number, digits = 1): string {
  return value.toFixed(digits);
}

function formatNps(score: number): string {
  return score > 0 ? `+${score}` : String(score);
}

function formatPercent(value: number): string {
  return `${value.toFixed(0)}%`;
}

function formatSignedDelta(value: number, formatValue: (v: number) => string): string {
  if (Math.abs(value) < 0.005) return "+0";
  return `${value > 0 ? "+" : ""}${formatValue(value)}`;
}

function replyTriageTotal(counts?: ReviewReplyStatusCounts | null): number {
  if (!counts) return 0;
  return num(counts.needingReply) + num(counts.replied) + num(counts.skipped);
}

/** Share of triage that is replied or skipped (not still needing a reply). */
function handledReplyRate(counts?: ReviewReplyStatusCounts | null): number | null {
  if (!counts) return null;
  const total = replyTriageTotal(counts);
  if (total <= 0) return null;
  return ((num(counts.replied) + num(counts.skipped)) / total) * 100;
}

function CellValue({
  value,
  coachValue,
  showCoachShare,
  deltaValue,
  formatDelta = formatInt,
}: {
  value: string;
  coachValue?: string | null;
  showCoachShare: boolean;
  deltaValue?: number | null;
  formatDelta?: (v: number) => string;
}) {
  const showCompanyTooltip = showCoachShare && coachValue != null;

  const content = (
    <span
      className={cn(
        "inline-flex flex-wrap items-baseline justify-end gap-x-1 tabular-nums text-foreground",
        showCompanyTooltip && "cursor-help"
      )}
    >
      <span>{value}</span>
      {deltaValue != null && (
        <span
          className={cn(
            "text-xs font-medium",
            Math.abs(deltaValue) < 0.005
              ? "text-muted-foreground"
              : deltaValue > 0
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-rose-600 dark:text-rose-400"
          )}
          title="vs prior period"
        >
          ({formatSignedDelta(deltaValue, formatDelta)})
        </span>
      )}
    </span>
  );

  if (!showCompanyTooltip) return content;

  return (
    <Tooltip>
      <Tooltip.Trigger render={content} />
      <Tooltip.Content className="font-sans">Company: {coachValue}</Tooltip.Content>
    </Tooltip>
  );
}

type ReviewNavFilters = Record<string, string>;

type MetricRow = {
  key: string;
  label: React.ReactNode;
  period: React.ReactNode;
  allTime: React.ReactNode;
  emphasize?: boolean;
  /** Reviews-tab URL filters applied when this metric is clicked */
  filters?: ReviewNavFilters;
  /** 0–1 share of the period total; renders a bar beside the label */
  ratio?: number | null;
  ratioBarClassName?: string;
};

type CountSeries = {
  key: string;
  label: React.ReactNode;
  period: number;
  previous: number;
  allTime: number;
  coachPeriod: number;
  coachAllTime: number;
  filters?: ReviewNavFilters;
  ratio?: number | null;
  ratioBarClassName?: string;
  emphasize?: boolean;
};

function countMetricRows(
  series: CountSeries[],
  {
    showCoachShare,
    hasPrevious,
  }: {
    showCoachShare: boolean;
    hasPrevious: boolean;
  }
): MetricRow[] {
  return series.map(row => ({
    key: row.key,
    label: row.label,
    filters: row.filters,
    ratio: row.ratio,
    ratioBarClassName: row.ratioBarClassName,
    emphasize: row.emphasize,
    period: (
      <CellValue
        value={formatInt(row.period)}
        coachValue={showCoachShare ? formatInt(row.coachPeriod) : null}
        showCoachShare={showCoachShare}
        deltaValue={delta(row.period, row.previous, hasPrevious)}
      />
    ),
    allTime: (
      <CellValue
        value={formatInt(row.allTime)}
        coachValue={showCoachShare ? formatInt(row.coachAllTime) : null}
        showCoachShare={showCoachShare}
      />
    ),
  }));
}

function ratioShare(count: number, total: number): number {
  if (total <= 0) return 0;
  return count / total;
}

/** Pull the same numeric field across period / previous / all-time / coach snapshots. */
function countAcross(
  key: keyof ReviewOverviewSnapshot,
  snapshot?: ReviewOverviewSnapshot | null,
  previousSnapshot?: ReviewOverviewSnapshot | null,
  allTimeSnapshot?: ReviewOverviewSnapshot | null,
  coachPeriodSnapshot?: ReviewOverviewSnapshot | null,
  coachAllTimeSnapshot?: ReviewOverviewSnapshot | null
): Pick<CountSeries, "period" | "previous" | "allTime" | "coachPeriod" | "coachAllTime"> {
  return {
    period: num(snapshot?.[key] as CountLike),
    previous: num(previousSnapshot?.[key] as CountLike),
    allTime: num(allTimeSnapshot?.[key] as CountLike),
    coachPeriod: num(coachPeriodSnapshot?.[key] as CountLike),
    coachAllTime: num(coachAllTimeSnapshot?.[key] as CountLike),
  };
}

type MetricAccent = {
  icon: LucideIcon;
  iconClassName: string;
  headerClassName: string;
  borderClassName: string;
  filledIcon?: boolean;
};

type MetricHeroConfig = {
  value: string;
  label: string;
  delta?: number | null;
  formatDelta?: (v: number) => string;
  allTimeValue?: string | null;
  filters?: ReviewNavFilters;
  graphic: React.ReactNode;
};

type MetricEmptyConfig = {
  title: string;
  description: string;
};

/** True when there is nothing to show for this metric (including all-time). */
function hasNoLifetimeData(periodCount: number, allTimeCount: number, isAllTimePeriod: boolean): boolean {
  if (isAllTimePeriod) return periodCount <= 0;
  return periodCount <= 0 && allTimeCount <= 0;
}

function MetricEmptyState({ title, description, onGenerateForm }: MetricEmptyConfig & { onGenerateForm: () => void }) {
  return (
    <div className="flex min-h-[16.5rem] flex-col items-center justify-center px-6 py-10 text-center">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 max-w-[16rem] text-xs leading-relaxed text-muted-foreground">{description}</p>
      <Button size="sm" variant="outline" className="mt-4" onClick={onGenerateForm}>
        <ClipboardList className="mr-1.5 h-4 w-4" />
        Create a new review form
      </Button>
    </div>
  );
}

function MetricTable({
  title,
  tip,
  rows,
  accent,
  hero,
  empty,
  onMetricClick,
  onGenerateForm,
  isAllTimePeriod,
}: {
  title: string;
  tip: string;
  rows: MetricRow[];
  accent: MetricAccent;
  hero?: MetricHeroConfig;
  empty?: MetricEmptyConfig | null;
  onMetricClick: (filters: ReviewNavFilters, options?: { allTime?: boolean }) => void;
  onGenerateForm: () => void;
  isAllTimePeriod?: boolean;
}) {
  const hasRatios = rows.some(row => row.ratio != null);
  const Icon = accent.icon;
  const showEmpty = empty != null;
  const tipText = showEmpty ? tip : `${tip} Click a row to open matching reviews.`;

  return (
    <div
      className={cn(
        "min-w-[min(100%,400px)] overflow-hidden rounded-2xl border bg-background font-sans",
        accent.borderClassName
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center px-4 pt-3",
          accent.headerClassName,
          (showEmpty || !hero) && "border-b border-border/50 pb-3"
        )}
      >
        <span
          className={cn(
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg shadow-sm ring-1 ring-inset ring-black/5 dark:ring-white/10",
            accent.iconClassName
          )}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2.25} fill={accent.filledIcon ? "currentColor" : "none"} />
        </span>
        <h3 className="m-0 ml-2 text-sm font-semibold uppercase leading-none tracking-[0.14em] text-muted-foreground">
          {title}
        </h3>
        <div className="ml-2 flex shrink-0 items-center leading-none">
          <InfoTip text={tipText} />
        </div>
      </div>
      {showEmpty ? (
        <MetricEmptyState {...empty} onGenerateForm={onGenerateForm} />
      ) : (
        <>
          {hero && (
            <div className={cn(accent.headerClassName)}>
              <MetricHero
                value={hero.value}
                label={hero.label}
                delta={isAllTimePeriod ? null : hero.delta}
                formatDelta={hero.formatDelta}
                allTimeValue={isAllTimePeriod ? null : hero.allTimeValue}
                graphic={hero.graphic}
                onClick={hero.filters != null ? () => onMetricClick(hero.filters!) : undefined}
              />
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2 text-left font-semibold"> </th>
                <th className="px-4 py-2 text-right font-semibold">{isAllTimePeriod ? "All time" : "This period"}</th>
                {!isAllTimePeriod && <th className="px-4 py-2 text-right font-semibold">All time</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const clickable = !!row.filters;
                return (
                  <tr
                    key={row.key}
                    className={cn(
                      "border-b border-border/40 last:border-b-0",
                      row.emphasize ? "bg-amber-100/90 dark:bg-amber-950/55" : clickable && "hover:bg-muted/45",
                      clickable && "cursor-pointer transition-colors",
                      row.emphasize && clickable && "hover:bg-amber-200/90 dark:hover:bg-amber-900/55"
                    )}
                    onClick={clickable ? () => onMetricClick(row.filters!) : undefined}
                    onKeyDown={
                      clickable
                        ? event => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onMetricClick(row.filters!);
                            }
                          }
                        : undefined
                    }
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? "link" : undefined}
                    aria-label={
                      clickable ? `View ${typeof row.label === "string" ? row.label : row.key} reviews` : undefined
                    }
                  >
                    <td
                      className={cn(
                        "px-4 py-2.5 text-muted-foreground",
                        row.emphasize && "font-semibold text-amber-950 dark:text-amber-100",
                        hasRatios && "w-[55%]"
                      )}
                    >
                      {row.ratio != null ? (
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span className="w-[5.25rem] shrink-0">{row.label}</span>
                          <RatioBar value={row.ratio} barClassName={row.ratioBarClassName} />
                          <span className="w-8 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                            {Math.round(row.ratio * 100)}%
                          </span>
                        </div>
                      ) : (
                        row.label
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">{row.period}</td>
                    {!isAllTimePeriod && (
                      <td
                        className={cn("px-4 py-2.5 text-right", clickable && "hover:underline")}
                        title={clickable ? "View all-time reviews" : undefined}
                        onClick={
                          clickable
                            ? event => {
                                event.stopPropagation();
                                onMetricClick(row.filters!, { allTime: true });
                              }
                            : undefined
                        }
                      >
                        {row.allTime}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

const METRIC_ACCENTS = {
  reviews: {
    icon: MessagesSquare,
    iconClassName: "bg-sky-500 text-white",
    headerClassName: "bg-gradient-to-r from-sky-50/90 to-transparent dark:from-sky-950/40",
    borderClassName: "border-sky-200/70 dark:border-sky-900/50",
  },
  stars: {
    icon: Star,
    iconClassName: "bg-amber-400 text-amber-950",
    headerClassName: "bg-gradient-to-r from-amber-50/90 to-transparent dark:from-amber-950/35",
    borderClassName: "border-amber-200/70 dark:border-amber-900/45",
    filledIcon: true,
  },
  nps: {
    icon: Gauge,
    iconClassName: "bg-violet-500 text-white",
    headerClassName: "bg-gradient-to-r from-violet-50/90 to-transparent dark:from-violet-950/40",
    borderClassName: "border-violet-200/70 dark:border-violet-900/50",
  },
  tenPoint: {
    icon: Hash,
    iconClassName: "bg-indigo-500 text-white",
    headerClassName: "bg-gradient-to-r from-indigo-50/90 to-transparent dark:from-indigo-950/40",
    borderClassName: "border-indigo-200/70 dark:border-indigo-900/50",
  },
  sentiment: {
    icon: Smile,
    iconClassName: "bg-emerald-500 text-white",
    headerClassName: "bg-gradient-to-r from-emerald-50/90 to-transparent dark:from-emerald-950/35",
    borderClassName: "border-emerald-200/70 dark:border-emerald-900/45",
  },
  replies: {
    icon: MessageCircleReply,
    iconClassName: "bg-teal-500 text-white",
    headerClassName: "bg-gradient-to-r from-teal-50/90 to-transparent dark:from-teal-950/35",
    borderClassName: "border-teal-200/70 dark:border-teal-900/45",
  },
} as const satisfies Record<string, MetricAccent>;

export function PeriodHealth({
  snapshot,
  previousSnapshot,
  allTimeSnapshot,
  coachPeriodSnapshot,
  coachAllTimeSnapshot,
  showCoachShare,
  isAllTimePeriod = false,
  volumeSeries,
  replyStatusCounts,
  previousReplyStatusCounts,
  allTimeReplyStatusCounts,
  coachPeriodReplyStatusCounts,
  coachAllTimeReplyStatusCounts,
  showTeamHighlights = true,
  exportFilters = [],
  disabled = false,
  isRefreshingSnapshot = false,
  onRefreshSnapshot,
  onSnapshotSynced,
  onNavigateToReviews,
}: {
  snapshot?: ReviewOverviewSnapshot | null;
  previousSnapshot?: ReviewOverviewSnapshot | null;
  allTimeSnapshot?: ReviewOverviewSnapshot | null;
  coachPeriodSnapshot?: ReviewOverviewSnapshot | null;
  coachAllTimeSnapshot?: ReviewOverviewSnapshot | null;
  showCoachShare: boolean;
  isAllTimePeriod?: boolean;
  volumeSeries?: VolumeSeries;
  replyStatusCounts?: ReviewReplyStatusCounts | null;
  previousReplyStatusCounts?: ReviewReplyStatusCounts | null;
  allTimeReplyStatusCounts?: ReviewReplyStatusCounts | null;
  coachPeriodReplyStatusCounts?: ReviewReplyStatusCounts | null;
  coachAllTimeReplyStatusCounts?: ReviewReplyStatusCounts | null;
  showTeamHighlights?: boolean;
  exportFilters?: string[];
  disabled?: boolean;
  isRefreshingSnapshot?: boolean;
  onRefreshSnapshot?: () => void;
  onSnapshotSynced?: (snapshot: ReviewOverviewSnapshot) => void | Promise<void>;
  onNavigateToReviews: (filters: ReviewNavFilters, options?: { allTime?: boolean }) => void;
}) {
  const [formCreateAIDialogOpen, setFormCreateAIDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formRequestDialogOpen, setFormRequestDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<Form | UpsertFormRequest | null>(null);

  const openCreateForm = () => {
    setSelectedForm(null);
    setFormCreateAIDialogOpen(true);
  };

  const handleCreateAISuccess = (draft: UpsertFormRequest) => {
    setFormCreateAIDialogOpen(false);
    setSelectedForm(draft);
    setFormDialogOpen(true);
  };

  const hasPrevious = !!previousSnapshot;
  const periodTotal = snapshot?.totalReviews ?? 0;
  const previousTotal = previousSnapshot?.totalReviews ?? 0;
  const allTimeTotal = allTimeSnapshot?.totalReviews ?? 0;
  const cellOpts = { showCoachShare, hasPrevious };

  const currentPositive = positiveSentimentPercent(snapshot) ?? 0;
  const previousPositive = positiveSentimentPercent(previousSnapshot);
  const allTimePositive = positiveSentimentPercent(allTimeSnapshot) ?? 0;
  const periodSentimentTotal =
    num(snapshot?.sentimentPositiveCount) +
    num(snapshot?.sentimentNeutralCount) +
    num(snapshot?.sentimentNegativeCount);
  const allTimeSentimentTotal =
    num(allTimeSnapshot?.sentimentPositiveCount) +
    num(allTimeSnapshot?.sentimentNeutralCount) +
    num(allTimeSnapshot?.sentimentNegativeCount);

  const currentHandledRate = handledReplyRate(replyStatusCounts) ?? 0;
  const previousHandledRate = handledReplyRate(previousReplyStatusCounts);
  const allTimeHandledRate = handledReplyRate(allTimeReplyStatusCounts) ?? 0;
  const periodReplyTotal = replyTriageTotal(replyStatusCounts);
  const allTimeReplyTotal = replyTriageTotal(allTimeReplyStatusCounts);

  const periodStarReviews = num(snapshot?.totalStarReviews);
  const allTimeStarReviews = num(allTimeSnapshot?.totalStarReviews);
  const periodNpsResponses = num(snapshot?.totalNpsResponses);
  const allTimeNpsResponses = num(allTimeSnapshot?.totalNpsResponses);
  const periodTenPointResponses = num(snapshot?.totalTenPointResponses);
  const allTimeTenPointResponses = num(allTimeSnapshot?.totalTenPointResponses);

  const avgStar = num(snapshot?.avgStarRating);
  const npsNet = num(snapshot?.npsNetScore);
  const avgTenPoint = num(snapshot?.avgTenPointScore);

  const reviewsEmpty = hasNoLifetimeData(periodTotal, allTimeTotal, isAllTimePeriod)
    ? {
        title: "No reviews yet",
        description: "Generate a feedback form to start collecting reviews and track performance here.",
      }
    : null;
  const repliesEmpty = hasNoLifetimeData(periodReplyTotal, allTimeReplyTotal, isAllTimePeriod)
    ? {
        title: "No replies yet",
        description: "Generate a form to collect reviews, then manage replies from this overview.",
      }
    : null;
  const sentimentEmpty = hasNoLifetimeData(periodSentimentTotal, allTimeSentimentTotal, isAllTimePeriod)
    ? {
        title: "No sentiment data yet",
        description: "Generate a form to collect feedback you can analyze for sentiment.",
      }
    : null;
  const starsEmpty = hasNoLifetimeData(periodStarReviews, allTimeStarReviews, isAllTimePeriod)
    ? {
        title: "No star ratings yet",
        description: "Add a star rating question to a form to track averages and distribution here.",
      }
    : null;
  const npsEmpty = hasNoLifetimeData(periodNpsResponses, allTimeNpsResponses, isAllTimePeriod)
    ? {
        title: "No NPS responses yet",
        description: "Add an NPS question to a form to measure promoter score and loyalty.",
      }
    : null;
  const tenPointEmpty = hasNoLifetimeData(periodTenPointResponses, allTimeTenPointResponses, isAllTimePeriod)
    ? {
        title: "No 10-point ratings yet",
        description: "Add a 10-point rating question to a form to track scores here.",
      }
    : null;

  const across = (key: keyof ReviewOverviewSnapshot) =>
    countAcross(key, snapshot, previousSnapshot, allTimeSnapshot, coachPeriodSnapshot, coachAllTimeSnapshot);

  const reviewSourceDefs = [
    { key: "GL" as const, label: "Google", field: "googleReviewCount" as const },
    { key: "FB" as const, label: "Facebook", field: "facebookReviewCount" as const },
    { key: "ZL" as const, label: "Zillow", field: "zillowReviewCount" as const },
    { key: "SC" as const, label: "SocialCoach", field: "socialCoachReviewCount" as const },
  ];

  const reviewSeries: ExportCountRow[] = reviewSourceDefs.map(source => ({
    metric: source.label,
    ...across(source.field),
  }));

  const reviewRows = countMetricRows(
    reviewSourceDefs.map(source => ({
      key: source.key,
      label: (
        <span className="inline-flex items-center gap-1.5">
          <ReviewSourceIcon sourceSystem={source.key} size="tiny" />
          {source.label}
        </span>
      ),
      filters: { sourceSystem: source.key },
      ...across(source.field),
    })),
    cellOpts
  );

  const starSeries: ExportCountRow[] = [
    { metric: "Responses", ...across("totalStarReviews") },
    ...(
      [
        { label: "5 star", field: "fiveStarCount" as const },
        { label: "4 star", field: "fourStarCount" as const },
        { label: "3 star", field: "threeStarCount" as const },
        { label: "2 star", field: "twoStarCount" as const },
        { label: "1 star", field: "oneStarCount" as const },
      ] as const
    ).map(row => {
      const counts = across(row.field);
      return {
        metric: row.label,
        share: ratioShare(counts.period, num(snapshot?.totalStarReviews)),
        ...counts,
      };
    }),
  ];

  const starRows: MetricRow[] = [
    ...countMetricRows(
      [
        {
          key: "total",
          label: "Responses",
          filters: { ratingType: "star" },
          ...across("totalStarReviews"),
        },
      ],
      cellOpts
    ),
    ...countMetricRows(
      (
        [
          { key: "5", label: "5 star", field: "fiveStarCount" },
          { key: "4", label: "4 star", field: "fourStarCount" },
          { key: "3", label: "3 star", field: "threeStarCount" },
          { key: "2", label: "2 star", field: "twoStarCount" },
          { key: "1", label: "1 star", field: "oneStarCount" },
        ] as const
      ).map(row => {
        const counts = across(row.field);
        return {
          key: row.key,
          label: row.label,
          filters: { ratingType: "star", ratingValue: row.key },
          ratio: ratioShare(counts.period, num(snapshot?.totalStarReviews)),
          ...counts,
        };
      }),
      cellOpts
    ),
  ];

  const npsSeries: ExportCountRow[] = [
    { metric: "Responses", ...across("totalNpsResponses") },
    ...(
      [
        { label: "Promoters", field: "npsPromoterCount" as const },
        { label: "Passives", field: "npsPassiveCount" as const },
        { label: "Detractors", field: "npsDetractorCount" as const },
      ] as const
    ).map(row => {
      const counts = across(row.field);
      return {
        metric: row.label,
        share: ratioShare(counts.period, num(snapshot?.totalNpsResponses)),
        ...counts,
      };
    }),
  ];

  const npsRows: MetricRow[] = [
    ...countMetricRows(
      [
        {
          key: "responses",
          label: "Responses",
          filters: { ratingType: "nps" },
          ...across("totalNpsResponses"),
        },
      ],
      cellOpts
    ),
    ...countMetricRows(
      (
        [
          {
            key: "promoter",
            label: "Promoters",
            field: "npsPromoterCount",
            barClassName: "bg-emerald-500 dark:bg-emerald-400",
          },
          {
            key: "passive",
            label: "Passives",
            field: "npsPassiveCount",
            barClassName: "bg-slate-400 dark:bg-slate-500",
          },
          {
            key: "detractor",
            label: "Detractors",
            field: "npsDetractorCount",
            barClassName: "bg-rose-500 dark:bg-rose-400",
          },
        ] as const
      ).map(row => {
        const counts = across(row.field);
        return {
          key: row.key,
          label: row.label,
          filters: { ratingType: "nps", npsCategory: row.key },
          ratio: ratioShare(counts.period, num(snapshot?.totalNpsResponses)),
          ratioBarClassName: row.barClassName,
          ...counts,
        };
      }),
      cellOpts
    ),
  ];

  const tenPointSeries: ExportCountRow[] = [{ metric: "Responses", ...across("totalTenPointResponses") }];

  const tenPointRows = countMetricRows(
    [
      {
        key: "responses",
        label: "Responses",
        filters: { ratingType: "ten_point" },
        ...across("totalTenPointResponses"),
      },
    ],
    cellOpts
  );

  const sentimentSeries: ExportCountRow[] = (
    [
      { label: "Positive", field: "sentimentPositiveCount" as const },
      { label: "Neutral", field: "sentimentNeutralCount" as const },
      { label: "Negative", field: "sentimentNegativeCount" as const },
    ] as const
  ).map(row => {
    const counts = across(row.field);
    return {
      metric: row.label,
      share: ratioShare(counts.period, periodSentimentTotal),
      ...counts,
    };
  });

  const sentimentRows = countMetricRows(
    (
      [
        {
          key: "positive",
          label: "Positive",
          field: "sentimentPositiveCount",
          barClassName: "bg-emerald-500 dark:bg-emerald-400",
        },
        {
          key: "neutral",
          label: "Neutral",
          field: "sentimentNeutralCount",
          barClassName: "bg-slate-400 dark:bg-slate-500",
        },
        {
          key: "negative",
          label: "Negative",
          field: "sentimentNegativeCount",
          barClassName: "bg-rose-500 dark:bg-rose-400",
        },
      ] as const
    ).map(row => {
      const counts = across(row.field);
      return {
        key: row.key,
        label: row.label,
        filters: { sentiment: row.key },
        ratio: ratioShare(counts.period, periodSentimentTotal),
        ratioBarClassName: row.barClassName,
        ...counts,
      };
    }),
    cellOpts
  );

  const replyTriageDefs = [
    {
      key: "needingReply",
      label: "Unreplied",
      triage: "needingReply" as const,
      barClassName: "bg-amber-400 dark:bg-amber-500",
    },
    {
      key: "replied",
      label: "Replied",
      triage: "replied" as const,
      barClassName: "bg-emerald-500 dark:bg-emerald-400",
    },
    {
      key: "skipped",
      label: "Skipped",
      triage: "skipped" as const,
      barClassName: "bg-slate-400 dark:bg-slate-500",
    },
  ] as const;

  const replySeries: ExportCountRow[] = replyTriageDefs.map(row => ({
    metric: row.label,
    share: ratioShare(num(replyStatusCounts?.[row.triage]), periodReplyTotal),
    period: num(replyStatusCounts?.[row.triage]),
    previous: num(previousReplyStatusCounts?.[row.triage]),
    allTime: num(allTimeReplyStatusCounts?.[row.triage]),
    coachPeriod: num(coachPeriodReplyStatusCounts?.[row.triage]),
    coachAllTime: num(coachAllTimeReplyStatusCounts?.[row.triage]),
  }));

  const replyRows = countMetricRows(
    replyTriageDefs.map(row => {
      const period = num(replyStatusCounts?.[row.triage]);
      return {
        key: row.key,
        label: row.label,
        filters: { triage: row.triage },
        ratio: ratioShare(period, periodReplyTotal),
        ratioBarClassName: row.barClassName,
        period,
        previous: num(previousReplyStatusCounts?.[row.triage]),
        allTime: num(allTimeReplyStatusCounts?.[row.triage]),
        coachPeriod: num(coachPeriodReplyStatusCounts?.[row.triage]),
        coachAllTime: num(coachAllTimeReplyStatusCounts?.[row.triage]),
        emphasize: row.key === "needingReply" && period > 0,
      };
    }),
    { showCoachShare, hasPrevious: hasPrevious && previousReplyStatusCounts != null }
  );

  const getExportSections = (): XlsxTableSection[] =>
    buildPeriodHealthExportSections({
      isAllTimePeriod,
      showCoachShare,
      showTeamHighlights,
      hasPrevious,
      hasPreviousReplies: hasPrevious && previousReplyStatusCounts != null,
      periodTotal,
      previousTotal,
      allTimeTotal,
      currentHandledRate,
      previousHandledRate,
      allTimeHandledRate,
      currentPositive,
      previousPositive,
      allTimePositive,
      avgStar,
      previousAvgStar: num(previousSnapshot?.avgStarRating),
      allTimeAvgStar: num(allTimeSnapshot?.avgStarRating),
      npsNet,
      previousNpsNet: num(previousSnapshot?.npsNetScore),
      allTimeNpsNet: num(allTimeSnapshot?.npsNetScore),
      avgTenPoint,
      previousAvgTenPoint: num(previousSnapshot?.avgTenPointScore),
      allTimeAvgTenPoint: num(allTimeSnapshot?.avgTenPointScore),
      reviewSeries,
      replySeries,
      sentimentSeries,
      starSeries,
      npsSeries,
      tenPointSeries,
      aiSummary: snapshot?.aiSummary,
      aiHighlights: snapshot?.aiHighlights,
      aiGeneratedAt: snapshot?.aiGeneratedAt,
    });

  return (
    <section className="font-sans" aria-busy={disabled}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <h2 className="text-lg font-semibold tracking-tight mb-0">Performance</h2>
          {onRefreshSnapshot && (
            <OverviewSnapshotSync
              snapshot={snapshot}
              isRefreshing={isRefreshingSnapshot}
              onRefresh={onRefreshSnapshot}
              onSnapshotSynced={onSnapshotSynced}
            />
          )}
        </div>
        <PeriodHealthExportButton
          getExportSections={getExportSections}
          filters={exportFilters}
          disabled={disabled || !snapshot}
        />
      </div>

      <div className={cn("transition-opacity", disabled && "pointer-events-none opacity-50")}>
        <div className="mb-6 empty:hidden">
          <PeriodInsights
            summary={snapshot?.aiSummary}
            highlights={snapshot?.aiHighlights}
            generatedAt={snapshot?.aiGeneratedAt}
            showTeamHighlights={showTeamHighlights}
          />
        </div>

        <div className="@container w-full">
          <div className="grid grid-cols-1 gap-4 @min-[816px]:grid-cols-2 @min-[1232px]:grid-cols-3">
            <MetricTable
              title="Reviews"
              tip={
                isAllTimePeriod ? "Review volume by source." : "Review volume by source for this period vs all time."
              }
              rows={reviewRows}
              accent={METRIC_ACCENTS.reviews}
              empty={reviewsEmpty}
              hero={{
                value: formatInt(periodTotal),
                label: "Total reviews",
                delta: delta(periodTotal, previousTotal, hasPrevious),
                formatDelta: formatInt,
                allTimeValue: formatInt(allTimeTotal),
                filters: {},
                graphic: (
                  <HeroVolumeGraphic
                    count={periodTotal}
                    points={volumeSeries?.points}
                    currentIndex={volumeSeries?.currentIndex}
                  />
                ),
              }}
              onMetricClick={onNavigateToReviews}
              onGenerateForm={openCreateForm}
              isAllTimePeriod={isAllTimePeriod}
            />
            <MetricTable
              title="Replies"
              tip={
                isAllTimePeriod
                  ? "Live reply triage counts and reply rate."
                  : "Live reply triage counts and reply rate for this period vs all time."
              }
              rows={replyRows}
              accent={METRIC_ACCENTS.replies}
              empty={repliesEmpty}
              hero={{
                value: formatPercent(currentHandledRate),
                label: "Replied / skipped",
                delta: previousHandledRate != null ? delta(currentHandledRate, previousHandledRate, hasPrevious) : null,
                formatDelta: v => `${v.toFixed(0)} pts`,
                allTimeValue: formatPercent(allTimeHandledRate),
                filters: {},
                graphic: (
                  <HeroReplyGraphic
                    replied={num(replyStatusCounts?.replied)}
                    skipped={num(replyStatusCounts?.skipped)}
                    needingReply={num(replyStatusCounts?.needingReply)}
                  />
                ),
              }}
              onMetricClick={onNavigateToReviews}
              onGenerateForm={openCreateForm}
              isAllTimePeriod={isAllTimePeriod}
            />
            <MetricTable
              title="Sentiment"
              tip="Positive sentiment share and distribution across sentiment."
              rows={sentimentRows}
              accent={METRIC_ACCENTS.sentiment}
              empty={sentimentEmpty}
              hero={{
                value: formatPercent(currentPositive),
                label: "Positive",
                delta: previousPositive != null ? delta(currentPositive, previousPositive, hasPrevious) : null,
                formatDelta: v => `${v.toFixed(0)} pts`,
                allTimeValue: formatPercent(allTimePositive),
                filters: { sentiment: "positive" },
                graphic: (
                  <HeroSentimentChart
                    positive={num(snapshot?.sentimentPositiveCount)}
                    neutral={num(snapshot?.sentimentNeutralCount)}
                    negative={num(snapshot?.sentimentNegativeCount)}
                  />
                ),
              }}
              onMetricClick={onNavigateToReviews}
              onGenerateForm={openCreateForm}
              isAllTimePeriod={isAllTimePeriod}
            />
            <MetricTable
              title="Star ratings"
              tip="Average star rating and share by star level."
              rows={starRows}
              accent={METRIC_ACCENTS.stars}
              empty={starsEmpty}
              hero={{
                value: formatDecimal(avgStar),
                label: "Average rating",
                delta: delta(avgStar, num(previousSnapshot?.avgStarRating), hasPrevious),
                formatDelta: formatDecimal,
                allTimeValue: formatDecimal(num(allTimeSnapshot?.avgStarRating)),
                filters: { ratingType: "star" },
                graphic: <HeroStarGraphic rating={avgStar} />,
              }}
              onMetricClick={onNavigateToReviews}
              onGenerateForm={openCreateForm}
              isAllTimePeriod={isAllTimePeriod}
            />
            <MetricTable
              title="NPS"
              tip="Net Promoter Score and share of promoters, passives, and detractors."
              rows={npsRows}
              accent={METRIC_ACCENTS.nps}
              empty={npsEmpty}
              hero={{
                value: formatNps(npsNet),
                label: "Net score",
                delta: delta(npsNet, num(previousSnapshot?.npsNetScore), hasPrevious),
                formatDelta: formatInt,
                allTimeValue: formatNps(num(allTimeSnapshot?.npsNetScore)),
                filters: { ratingType: "nps" },
                graphic: <HeroNpsGauge score={npsNet} />,
              }}
              onMetricClick={onNavigateToReviews}
              onGenerateForm={openCreateForm}
              isAllTimePeriod={isAllTimePeriod}
            />
            <MetricTable
              title="10-point ratings"
              tip="Average and count of 10-point scale responses."
              rows={tenPointRows}
              accent={METRIC_ACCENTS.tenPoint}
              empty={tenPointEmpty}
              hero={{
                value: formatDecimal(avgTenPoint),
                label: "Average rating",
                delta: delta(avgTenPoint, num(previousSnapshot?.avgTenPointScore), hasPrevious),
                formatDelta: formatDecimal,
                allTimeValue: formatDecimal(num(allTimeSnapshot?.avgTenPointScore)),
                filters: { ratingType: "ten_point" },
                graphic: <HeroTenPointScale average={avgTenPoint} />,
              }}
              onMetricClick={onNavigateToReviews}
              onGenerateForm={openCreateForm}
              isAllTimePeriod={isAllTimePeriod}
            />
          </div>
        </div>
      </div>

      <FormCreateAIDialog
        open={formCreateAIDialogOpen}
        onOpenChange={setFormCreateAIDialogOpen}
        onSuccess={handleCreateAISuccess}
      />
      <FormDialog
        open={formDialogOpen}
        onOpenChange={open => {
          setFormDialogOpen(open);
          if (!open) setSelectedForm(null);
        }}
        form={selectedForm}
        onCreateRequestClick={createdForm => {
          setFormDialogOpen(false);
          setSelectedForm(createdForm);
          setFormRequestDialogOpen(true);
        }}
      />
      <FormRequestDialog
        open={formRequestDialogOpen}
        onOpenChange={open => {
          setFormRequestDialogOpen(open);
          if (!open) setSelectedForm(null);
        }}
        form={selectedForm && "id" in selectedForm ? selectedForm : null}
      />
    </section>
  );
}
